import prettyBytes from 'pretty-bytes';
import { Paperclip } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { AttachmentView } from '../types';

interface Props {
  attachments: AttachmentView[];
}

// First-class attachments panel on the issue detail. Sourced from the
// backend's issue_attachments join (see IssueDetailResponse.attachments)
// rather than scraped out of the markdown body — the body still carries
// the links for Gitea, but the portal UI reads the structured list so
// filenames + sizes render cleanly.
export function AttachmentsList({ attachments }: Props) {
  if (attachments.length === 0) return null;

  return (
    <Card data-testid="issue-attachments-list">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Paperclip className="h-4 w-4" aria-hidden="true" />
          Attachments
          <span className="text-xs font-normal text-muted-foreground">({attachments.length})</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="divide-y rounded-md border">
          {attachments.map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-3 p-2.5"
              data-testid={`issue-attachment-${a.id}`}
            >
              <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                {/* Not asChild — this is a real anchor, not a routed link.
                    The signed URL includes an HMAC token and points at the
                    backend's public /attachments/:id endpoint. */}
                <a
                  href={a.url}
                  className="truncate text-sm font-medium text-primary hover:underline"
                  data-testid={`issue-attachment-link-${a.id}`}
                >
                  {a.filename}
                </a>
                <div className="text-xs text-muted-foreground">{prettyBytes(a.sizeBytes)}</div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
